// ── Hierarchical Tree View — functional collapsible tree ──
// Renders Site → Campaign → Area → Inspection as a navigable tree.

import { useState, useMemo, useCallback } from "react";
import type { GraphNode, GraphData } from "../types/graph";
import { severityColor } from "../utils/colors";

interface HierarchyViewProps {
  graphData: GraphData;
  onNodeClick: (node: GraphNode) => void;
  selectedNodeId: string | null;
}

// ── Build tree structure from flat graph data ──

interface TreeNode {
  node: GraphNode;
  children: TreeNode[];
}

function buildTree(graphData: GraphData): TreeNode[] {
  const nodeMap = new Map<string, GraphNode>();
  for (const n of graphData.nodes) nodeMap.set(n.id, n);

  // Build parent→children map from links
  const childrenMap = new Map<string, string[]>();
  const hasParent = new Set<string>();

  for (const link of graphData.links) {
    const src = typeof link.source === "string" ? link.source : (link.source as unknown as GraphNode).id;
    const tgt = typeof link.target === "string" ? link.target : (link.target as unknown as GraphNode).id;
    const type = link.type;

    // Only structural/hierarchical links
    if (
      type === "HAS_CAMPAIGN" ||
      type === "has-campaign" ||
      type === "HAS_AREA" ||
      type === "has-area" ||
      type === "CONTAINS_AREA" ||
      type === "HAS_INSPECTION" ||
      type === "has-inspection" ||
      type === "CONTAINS"
    ) {
      const children = childrenMap.get(src) ?? [];
      children.push(tgt);
      childrenMap.set(src, children);
      hasParent.add(tgt);
    }
  }

  // Roots = nodes without a parent in the hierarchy
  const roots = graphData.nodes.filter((n) => !hasParent.has(n.id));

  function buildSubtree(nodeId: string): TreeNode | null {
    const node = nodeMap.get(nodeId);
    if (!node) return null;
    const childIds = childrenMap.get(nodeId) ?? [];
    const children = childIds
      .map((id) => buildSubtree(id))
      .filter(Boolean) as TreeNode[];

    // Sort children: campaigns by period desc, areas alphabetically, inspections by severity desc
    children.sort((a, b) => {
      if (a.node.type === "campaign" && b.node.type === "campaign") {
        return (b.node.meta?.period ?? "").localeCompare(a.node.meta?.period ?? "");
      }
      if (a.node.type === "area" && b.node.type === "area") {
        return a.node.label.localeCompare(b.node.label);
      }
      if (a.node.type === "inspection" && b.node.type === "inspection") {
        return (b.node.severity ?? 0) - (a.node.severity ?? 0);
      }
      return 0;
    });

    return { node, children };
  }

  return roots
    .map((r) => buildSubtree(r.id))
    .filter(Boolean) as TreeNode[];
}

// ── Severity Badge ──

function SeverityDot({ severity }: { severity?: number }) {
  if (severity === undefined) return null;
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: severityColor(severity) }}
      title={`Severity ${severity.toFixed(1)}`}
    />
  );
}

// ── Type icon ──

const TYPE_ICONS: Record<string, string> = {
  site: "🏗️",
  campaign: "📋",
  area: "📍",
  element: "🔩",
  inspection: "🔍",
};

// ── Stats pill ──

function StatsPill({ label, value }: { label: string; value: string | number }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-fl-sand-20 text-fl-evergreen-60 border border-fl-evergreen-20">
      <span className="text-fl-evergreen-40">{label}</span>
      <span className="font-medium text-fl-evergreen">{value}</span>
    </span>
  );
}

// ── Tree Node Row ──

function TreeRow({
  treeNode,
  depth,
  onNodeClick,
  selectedNodeId,
  defaultExpanded,
}: {
  treeNode: TreeNode;
  depth: number;
  onNodeClick: (node: GraphNode) => void;
  selectedNodeId: string | null;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { node, children } = treeNode;
  const hasChildren = children.length > 0;
  const isSelected = node.id === selectedNodeId;

  const handleToggle = useCallback(() => {
    if (hasChildren) setExpanded((prev) => !prev);
  }, [hasChildren]);

  const handleClick = useCallback(() => {
    onNodeClick(node);
  }, [onNodeClick, node]);

  return (
    <div>
      {/* Row */}
      <div
        className={`group flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer
          transition-all duration-150 hover:bg-fl-sand-20
          ${isSelected ? "bg-fl-leaf/10 ring-1 ring-fl-leaf/30" : ""}`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={handleClick}
      >
        {/* Expand/collapse toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          className={`w-5 h-5 flex items-center justify-center rounded text-xs transition-transform
            ${hasChildren ? "text-fl-evergreen-60 hover:text-fl-evergreen hover:bg-fl-evergreen-20" : "text-transparent"}
            ${expanded ? "rotate-90" : ""}`}
        >
          ▶
        </button>

        {/* Type icon */}
        <span className="text-sm flex-shrink-0">{TYPE_ICONS[node.type] ?? "●"}</span>

        {/* Severity dot */}
        <SeverityDot severity={node.severity} />

        {/* Label */}
        <span
          className={`text-sm truncate flex-1 ${
            isSelected ? "font-semibold text-fl-evergreen" : "text-fl-evergreen-80"
          }`}
        >
          {node.label}
        </span>

        {/* Stats */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {node.childCount !== undefined && (
            <StatsPill label="items" value={node.childCount} />
          )}
          {node.meta?.avgSeverity !== undefined && (
            <StatsPill label="avg" value={node.meta.avgSeverity.toFixed(1)} />
          )}
          {node.meta?.inspector && (
            <StatsPill label="" value={node.meta.inspector} />
          )}
        </div>

        {/* Type badge */}
        <span className="text-[10px] uppercase tracking-wider text-fl-evergreen-40 font-medium w-16 text-right flex-shrink-0">
          {node.type}
        </span>
      </div>

      {/* Children — animate expand/collapse */}
      {hasChildren && expanded && (
        <div className="border-l border-fl-evergreen-20 ml-5" style={{ marginLeft: `${depth * 20 + 18}px` }}>
          {children.map((child) => (
            <TreeRow
              key={child.node.id}
              treeNode={child}
              depth={depth + 1}
              onNodeClick={onNodeClick}
              selectedNodeId={selectedNodeId}
              defaultExpanded={depth < 1} // Auto-expand first 2 levels
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary Stats Bar ──

function SummaryBar({ graphData }: { graphData: GraphData }) {
  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalSeverity = 0;
    let sevCount = 0;
    for (const n of graphData.nodes) {
      counts[n.type] = (counts[n.type] ?? 0) + 1;
      if (n.severity !== undefined) {
        totalSeverity += n.severity;
        sevCount++;
      }
    }
    return {
      counts,
      avgSeverity: sevCount > 0 ? totalSeverity / sevCount : 0,
      total: graphData.nodes.length,
    };
  }, [graphData]);

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-fl-evergreen-20 bg-fl-sand-20/50">
      <span className="text-xs font-medium text-fl-evergreen">Summary</span>
      <div className="flex items-center gap-2">
        {stats.counts.site && <StatsPill label="Sites" value={stats.counts.site} />}
        {stats.counts.campaign && <StatsPill label="Campaigns" value={stats.counts.campaign} />}
        {stats.counts.area && <StatsPill label="Areas" value={stats.counts.area} />}
        {stats.counts.inspection && <StatsPill label="Inspections" value={stats.counts.inspection} />}
        <StatsPill label="Avg Sev" value={stats.avgSeverity.toFixed(1)} />
      </div>
    </div>
  );
}

// ── Search Bar ──

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="px-4 py-2 border-b border-fl-evergreen-20">
      <div className="flex items-center gap-2 bg-white rounded-lg border border-fl-evergreen-20 px-3 py-1.5">
        <span className="text-fl-evergreen-40 text-sm">🔎</span>
        <input
          type="text"
          placeholder="Search nodes..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 text-sm bg-transparent outline-none text-fl-evergreen placeholder:text-fl-evergreen-40"
        />
        {value && (
          <button
            onClick={() => onChange("")}
            className="text-fl-evergreen-40 hover:text-fl-evergreen text-sm"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Export ──

export default function HierarchyView({
  graphData,
  onNodeClick,
  selectedNodeId,
}: HierarchyViewProps) {
  const [search, setSearch] = useState("");

  const tree = useMemo(() => buildTree(graphData), [graphData]);

  // Filter tree if search is active
  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    const term = search.toLowerCase();

    function filterSubtree(node: TreeNode): TreeNode | null {
      const matchesSelf = node.node.label.toLowerCase().includes(term) ||
        node.node.type.toLowerCase().includes(term) ||
        node.node.meta?.category?.toLowerCase().includes(term) ||
        node.node.meta?.element?.toLowerCase().includes(term) ||
        node.node.meta?.inspector?.toLowerCase().includes(term);

      const filteredChildren = node.children
        .map(filterSubtree)
        .filter(Boolean) as TreeNode[];

      if (matchesSelf || filteredChildren.length > 0) {
        return { node: node.node, children: matchesSelf ? node.children : filteredChildren };
      }
      return null;
    }

    return tree.map(filterSubtree).filter(Boolean) as TreeNode[];
  }, [tree, search]);

  return (
    <div className="absolute inset-0 top-14 bottom-8 flex flex-col bg-white overflow-hidden">
      <SummaryBar graphData={graphData} />
      <SearchBar value={search} onChange={setSearch} />

      {/* Tree */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {filteredTree.length === 0 ? (
          <div className="flex items-center justify-center h-full text-fl-evergreen-40 text-sm">
            {search ? "No matching nodes found" : "No data loaded"}
          </div>
        ) : (
          filteredTree.map((root) => (
            <TreeRow
              key={root.node.id}
              treeNode={root}
              depth={0}
              onNodeClick={onNodeClick}
              selectedNodeId={selectedNodeId}
              defaultExpanded={true}
            />
          ))
        )}
      </div>
    </div>
  );
}
